import { Building } from "../Building";
import { BuildingGeometry } from "../BuildingGeometry";
import { THREEUtils } from "../../utils/THREEUtils";
import { Main } from "../../Main";
import { Vector2, Object3D, BufferGeometry } from "three";
import { WallAddon } from "../../scene/addon/WallAddon";
import { ISceneAddon } from "../../scene/addon/ISceneAddon";
import { WallContainer } from "./WallContainer";

/**
 * Manages walls in the building
 */
export class BuildingWall
{
	private _building : Building;
	private _external : WallContainer;
	private _internal : WallContainer;

	private _hidden : boolean = true;

	public sideWallGeometry : BufferGeometry;
	public endWallGeometryFront: BufferGeometry;
	public endWallGeometryBack: BufferGeometry;
	public endRoofGeometry: BufferGeometry;

	constructor(building: Building)
	{
		this._building = building;
		this.init();
	}

	private init()
	{
		this.sideWallGeometry = BuildingGeometry.createWallGeometry();
		this.endWallGeometryFront = BuildingGeometry.createWallGeometry();
		this.endWallGeometryBack  = BuildingGeometry.createWallGeometry();
		this.endRoofGeometry  = BuildingGeometry.createEndRoofGeometry(true);

		this._external = new WallContainer({
			sideGeometry: this.sideWallGeometry,
			endGeometryFront: this.endWallGeometryFront,
			endGeometryBack: this.endWallGeometryBack,
			endRoofGeometry: this.endRoofGeometry,
			material: this._building.materials.wall
		});

		this._internal = new WallContainer({
			sideGeometry: this.sideWallGeometry,
			endGeometryFront: this.endWallGeometryFront.clone(), // We need clones because WallContainer.update() modifies the geometry
			endGeometryBack: this.endWallGeometryBack.clone(),
			endRoofGeometry: this.endRoofGeometry,
			material: this._building.materials.interior
		});
	}

	public update()
	{
		const metrics = this._building.config.metrics;

		THREEUtils.scaleUVs(this.sideWallGeometry, new Vector2(metrics.length, metrics.height));
		THREEUtils.scaleUVs(this.endRoofGeometry, new Vector2(metrics.width,  metrics.roof_height));

		THREEUtils.scaleUVs(this._external.endGeometryFront,  new Vector2(metrics.width,  metrics.height));
		THREEUtils.scaleUVs(this._external.endGeometryBack,  new Vector2(metrics.width,  metrics.height));
		THREEUtils.scaleUVs(this._internal.endGeometryFront,  new Vector2(metrics.width,  metrics.height));
		THREEUtils.scaleUVs(this._internal.endGeometryBack,  new Vector2(metrics.width,  metrics.height));

		this.scaleWallRoofs(this._external, false);
		this.scaleWallRoofs(this._internal, true);

		Main.getInstance().render = true;
	}

	private scaleWallRoofs(wallContainer:WallContainer, internal:boolean)
	{
		const metrics = this._building.config.metrics;
		const bottom = internal ? 0 : this._building.config.building.wainscot.value;

		wallContainer.update(metrics, metrics.height, bottom);
	}

	public addNode(node:WallAddon)
	{
		// these will cut holes into the wall
		this._external.addNode(node);
		this._internal.addNode(node);
	}

	public updateCuts(nodes:{[wallId:string]:ISceneAddon[]})
	{
		this._external.updateCuts(nodes);
		this._internal.updateCuts(nodes);
	}

	public add(scene:Object3D)
	{
		this._external.add(scene);
		this._internal.add(scene);
	}

	public remove()
	{
		this._external.remove();
		this._internal.remove();
	}

	public setHidden(value: boolean)
	{
		this._hidden = value;

		if (value)
		{
			this._external.hide();
			this._internal.hide();
		}
		else
		{
			this._external.show();
			this._internal.show();
		}
	}
}